import { useMsalAccount } from "@shared/auth/hooks/useMsalAccount";
import { ErrorState } from "@shared/common/components/State/ErrorState";
import { LoadingState } from "@shared/common/components/State/LoadingState";
import KnopLogo from "@shared/assets/KnopLogo.svg?react";
import styled from "styled-components";
import { config } from "@shared/envVariables.ts";
import { ReactElement, ReactNode, useId } from "react";
import { ToggleableDialog, ToggleableDialogProps } from "./ToggleableDialog";
import { DateTime } from "luxon";
import { useAboutInfoQuery } from "./useAboutInfoQuery";

export function AboutDialog({ dialogRef }: Readonly<ToggleableDialogProps>): React.JSX.Element {
  const { account } = useMsalAccount();

  const {
    data: aboutInfo,
    dataUpdatedAt: infoUpdatedAt,
    isFetching,
    isError,
  } = useAboutInfoQuery();
  const queryLastUpdated = DateTime.fromMillis(infoUpdatedAt);

  return (
    <ToggleableDialog dialogRef={dialogRef} size="m" heading="Over KnoP">
      {isFetching && <LoadingState message="App info wordt opgehaald..." fullPage={false} />}
      {isError && <ErrorState />}
      {aboutInfo && (
        <Container>
          <Logo>
            <KnopLogo />
          </Logo>
          <div>
            Welkom {account?.name}
            <dl>
              <Definition title="Aangevraagd op" description={queryLastUpdated?.toUTC().toISO()} />
              <Definition title="KnoP UI" description={config.appVersion} />
              {aboutInfo.versions.map((v) => (
                <Definition key={v.componentName} title={v.componentName} description={v.version} />
              ))}
            </dl>
          </div>
        </Container>
      )}
    </ToggleableDialog>
  );
}

interface DefinitionProps {
  readonly title: string;
  readonly description: ReactNode | ReactNode[];
}
function Definition({ title, description }: DefinitionProps): ReactElement {
  const id = useId();
  const descriptions = Array.isArray(description) ? description : [description];
  return (
    <StyledDefinition>
      <StyledDt id={id}>{title}</StyledDt>
      <MultiLine>
        {
          // oxlint-disable-next-line react/jsx-key
          descriptions.map((desc) => (
            <dd aria-labelledby={id}>{desc}</dd>
          ))
        }
      </MultiLine>
    </StyledDefinition>
  );
}

const StyledDefinition = styled.div`
  display: flex;
  gap: 30px;
  margin-bottom: 5px;
`;

const StyledDt = styled.dt`
  font-weight: ${({ theme }) => theme.typography.weightMedium};
  min-width: 120px;
  line-height: 21px;
  &:after {
    content: ":";
  }
`;

const MultiLine = styled.div`
  display: flex;
  flex-direction: column;
  gap: 5px;

  > dd {
    margin: 0;
  }
`;

const Logo = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 20px;

  svg {
    stroke: inherit;
    fill: inherit;
  }
`;

const Container = styled.div`
  color: ${(props) => props.theme.colors.contentEmphasis};
  > div {
    padding: 0 20px;
  }
`;
